import { clsx } from 'clsx';
import type { ReactNode } from 'react';

/**
 * Shared style for the quiet, auto-width secondary action buttons used across
 * the settings forms (update/add email, change password), so their weight and
 * width stay consistent. Meant to override FormSubmitButton's default dark fill.
 */
export const settingsButtonClass =
  'w-auto border border-zinc-900/10 bg-transparent px-3 py-1.5 font-medium text-zinc-700 shadow-none hover:bg-zinc-100 focus-visible:outline-emerald-600 dark:border-white/10 dark:bg-transparent dark:text-zinc-200 dark:hover:bg-white/10';

/**
 * A titled group of settings rows, rendered as a bordered card (see the
 * account-settings redesign). Section headings are a small, uppercase, muted
 * eyebrow; rows are separated by hairline dividers. The `danger` variant tints
 * the card border and heading red for the destructive "Danger zone" group.
 */
export function SettingsSection({
  title,
  danger = false,
  children,
}: {
  title: string;
  danger?: boolean;
  children: ReactNode;
}) {
  return (
    <section
      className={clsx(
        'overflow-hidden rounded-2xl border bg-white shadow-sm dark:bg-zinc-900/60',
        danger
          ? 'border-red-500/30 dark:border-red-500/25'
          : 'border-zinc-950/10 dark:border-white/10',
      )}
    >
      <h2
        className={clsx(
          'px-5 pt-5 pb-1 font-semibold text-xs uppercase tracking-wider sm:px-6',
          danger ? 'text-red-600 dark:text-red-400' : 'text-zinc-500 dark:text-zinc-400',
        )}
      >
        {title}
      </h2>
      <dl className="divide-y divide-zinc-900/5 dark:divide-white/5">{children}</dl>
    </section>
  );
}

export default SettingsSection;
